import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarPlus,
  Copy,
  Download,
  Link2,
  QrCode,
  RefreshCw,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Input } from '@/components/ui/Field.js';
import { ConfirmDialog } from '@/components/ui/Modal.js';
import { useToast } from '@/components/ui/Toast.js';
import { dateOnly, dateTime } from '@/lib/format.js';
import { errorMessage } from '@/lib/api-error.js';
import { queryKeys } from '@/lib/query-keys.js';
import { CertificateFormModal } from './CertificateFormModal.js';
import { CertificateQrModal } from './CertificateQrModal.js';
import { VerdictBadge } from './CertificatesPage.js';
import { certificatesApi, type CertificateRecord } from './certificates-api.js';

/**
 * Sertifikat kartochkasi — barcha amallar shu yerda.
 *
 * Ro'yxatda amal tugmalari YO'Q: bekor qilish qaytarib bo'lmaydigan
 * amal va u tasodifan bosilib ketmasligi kerak (TZ 4.4).
 */
export function CertificateDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  const query = useQuery({
    queryKey: queryKeys.certificates.one(id),
    queryFn: () => certificatesApi.one(id),
    enabled: id !== '',
  });

  const record = query.data;

  function refresh(updated: CertificateRecord): void {
    queryClient.setQueryData(queryKeys.certificates.one(id), updated);
    void queryClient.invalidateQueries({ queryKey: queryKeys.certificates.all });
  }

  const regenerate = useMutation({
    mutationFn: () => certificatesApi.regenerate(id),
    onSuccess: (updated) => {
      refresh(updated);
      toast.success('PDF qayta shakllantirildi');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const revoke = useMutation({
    mutationFn: () => certificatesApi.revoke(id, revokeReason.trim() || null),
    onSuccess: (updated) => {
      refresh(updated);
      setRevokeOpen(false);
      toast.success('Sertifikat bekor qilindi — saytda darhol ko‘rinadi');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const extend = useMutation({
    mutationFn: () => certificatesApi.extend(id),
    onSuccess: (updated) => {
      refresh(updated);
      setExtendOpen(false);
      toast.success('Muddat uzaytirildi va PDF yangilandi');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const download = useMutation({
    mutationFn: () => certificatesApi.downloadPdf(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sertifikat-${record?.number ?? id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  async function copyLink(): Promise<void> {
    if (!record) return;
    try {
      await navigator.clipboard.writeText(record.verifyUrl);
      toast.success('Havola nusxalandi');
    } catch {
      toast.error('Havolani nusxalab bo‘lmadi — uni qo‘lda belgilab oling');
    }
  }

  if (query.isLoading) {
    return <p className="px-6 py-16 text-center text-sm text-n-600">Yuklanmoqda…</p>;
  }
  if (!record) {
    return <p className="px-6 py-16 text-center text-sm text-n-600">Sertifikat topilmadi.</p>;
  }

  const revoked = record.status === 'revoked';

  return (
    <div className="flex flex-col gap-[18px]">
      <Link
        to="/certificates"
        className="inline-flex w-fit items-center gap-1.5 text-[13.5px] text-brand-600 hover:text-accent-600"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Sertifikatlar ro‘yxati
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-mono text-[26px] font-semibold tracking-[-0.02em] text-n-900">
              {record.number}
            </h1>
            <VerdictBadge record={record} />
          </div>
          <p className="mt-1.5 text-[13.5px] text-n-600">{record.fullName}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            Tahrirlash
          </Button>
          <Button
            variant="outline"
            icon={<RefreshCw className="size-4" />}
            loading={regenerate.isPending}
            onClick={() => regenerate.mutate()}
          >
            Shakllantirish
          </Button>
          <Button
            variant="outline"
            icon={<Download className="size-4" />}
            loading={download.isPending}
            disabled={!record.pdfMediaId}
            onClick={() => download.mutate()}
          >
            Yuklab olish
          </Button>
        </div>
      </div>

      <div className="grid gap-[18px] lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader title="Ma’lumotlar" />
          <CardBody className="grid gap-x-6 gap-y-3.5 sm:grid-cols-2">
            <Row label="F.I.Sh." value={record.fullName} />
            <Row label="Kurs" value={record.courseTitle} />
            <Row label="Soat hajmi" value={String(record.hours)} />
            <Row
              label="O‘qish muddati"
              value={`${dateOnly(record.studyStart)} — ${dateOnly(record.studyEnd)}`}
            />
            <Row label="Berilgan" value={dateOnly(record.issuedAt)} />
            <Row label="Amal qiladi" value={dateOnly(record.expiresAt)} />
            {record.extendedUntil && (
              <Row label="Uzaytirildi" value={dateOnly(record.extendedUntil)} />
            )}
            <Row
              label="PDF shakllangan"
              value={record.pdfGeneratedAt ? dateTime(record.pdfGeneratedAt) : 'Hali yo‘q'}
            />
            {revoked && (
              <>
                <Row label="Bekor qilingan" value={dateTime(record.revokedAt)} />
                <Row label="Sabab" value={record.revokeReason ?? '—'} />
              </>
            )}
          </CardBody>
        </Card>

        <div className="flex flex-col gap-[18px]">
          <Card>
            <CardHeader
              title="Tekshirish havolasi"
              description="QR shu manzilga olib boradi. Raqamdan yasalmagan — taxmin qilib bo‘lmaydi."
            />
            <CardBody className="flex flex-col gap-3">
              <code className="block break-all border border-n-200 bg-n-50 px-3 py-2 text-[12px] text-n-800">
                {record.verifyUrl}
              </code>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Copy className="size-4" />}
                  onClick={() => void copyLink()}
                >
                  Havolani nusxalash
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<QrCode className="size-4" />}
                  onClick={() => setQrOpen(true)}
                >
                  QR ni ko‘rsatish
                </Button>
                <a
                  href={record.verifyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-2 text-[13px] text-brand-600 underline underline-offset-2"
                >
                  <Link2 className="size-4" aria-hidden />
                  Saytda ochish
                </a>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Amallar" />
            <CardBody className="flex flex-col gap-2">
              <Button
                variant="outline"
                icon={<CalendarPlus className="size-4" />}
                disabled={revoked}
                onClick={() => setExtendOpen(true)}
              >
                Muddatni uzaytirish (+3 yil)
              </Button>
              <Button
                variant="danger"
                icon={<Ban className="size-4" />}
                disabled={revoked}
                onClick={() => {
                  setRevokeReason('');
                  setRevokeOpen(true);
                }}
              >
                {revoked ? 'Bekor qilingan' : 'Bekor qilish'}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>

      <CertificateFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        record={record}
        onSaved={refresh}
      />

      <CertificateQrModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        verifyUrl={record.verifyUrl}
        number={record.number}
      />

      <ConfirmDialog
        open={revokeOpen}
        title="Sertifikatni bekor qilish"
        danger
        confirmLabel="Bekor qilish"
        loading={revoke.isPending}
        onCancel={() => setRevokeOpen(false)}
        onConfirm={() => revoke.mutate()}
        message={
          <div className="flex flex-col gap-3">
            <p>
              <b className="font-mono">{record.number}</b> — {record.fullName}. Saytdagi tekshirish
              sahifasida darhol «Bekor qilingan» deb ko‘rinadi. Bu amalni ortga qaytarib bo‘lmaydi.
            </p>
            <label className="flex flex-col gap-1 text-[13px] text-n-800">
              Sabab (ichki eslatma, saytga chiqmaydi)
              <Input
                value={revokeReason}
                onChange={(event) => setRevokeReason(event.target.value)}
                placeholder="Masalan: xato ma’lumot bilan berilgan"
              />
            </label>
          </div>
        }
      />

      <ConfirmDialog
        open={extendOpen}
        title="Muddatni uzaytirish"
        confirmLabel="Uzaytirish"
        loading={extend.isPending}
        onCancel={() => setExtendOpen(false)}
        onConfirm={() => extend.mutate()}
        message={
          <p>
            Amal qilish muddati {dateOnly(record.extendedUntil ?? record.expiresAt)} dan yana 3
            yilga uzaytiriladi va PDF qaytadan shakllanadi.
          </p>
        }
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[12px] text-n-600">{label}</span>
      <span className="text-[13.5px] text-n-900">{value}</span>
    </div>
  );
}
